import { defer, EMPTY, Observable, timer, merge } from 'rxjs';
import { expand, filter, finalize, share, switchMap, take, takeWhile, timeout } from 'rxjs/operators';

export interface PollUntilOptions {
  delayMs: number;
  setLoading?: (isLoading: boolean) => void;
  emitWhenDoneOnly?: boolean;
  timeoutMs?: number;
}

/**
 * Polls by invoking `factory` until `isDone` returns true.
 *
 * - Invokes `factory` immediately, then repeats after `delayMs` if not done.
 * - Stops after emitting the first value for which `isDone` returns true.
 * - By default, emits every polled value (including the final done value).
 * - If `emitWhenDoneOnly` is true, suppresses intermediate values and emits only the done value.
 * - If `setLoading` is provided, it is set to true only during each individual attempt.
 * - If `timeoutMs` is provided, the polling operation must reach the done condition
 *   within the specified time or the observable errors.
 */
export function pollUntil<T>(
  factory: () => Observable<T>,
  isDone: (value: T) => boolean,
  options: PollUntilOptions
): Observable<T> {
  const runAttempt = () =>
    defer(() => {
      options.setLoading?.(true);
      return factory().pipe(finalize(() => options.setLoading?.(false)));
    });

  // polling observable, emits every value
  const poll$ = runAttempt().pipe(
    expand(value => {
      if (isDone(value)) {
        return EMPTY;
      }

      return timer(options.delayMs).pipe(switchMap(() => runAttempt()));
    }),
    // emit values until done, including the done value
    takeWhile(value => !isDone(value), true),
    share()
  );

  // done observable, emits a single done value
  let done$ = poll$.pipe(
    filter(value => isDone(value)),
    take(1)
  );

  // optionally wrap done observable in a timeout
  if (options.timeoutMs) {
    done$ = done$.pipe(
      timeout({ first: options.timeoutMs })
    );
  }

  // if only 'done' emission needed, return done observable
  if (options.emitWhenDoneOnly) {
    return done$;
  }

  // progress observable, emits all not-done values
  const progress$ = poll$.pipe(
    filter(value => !isDone(value))
  );

  // merge progress and done observables
  return merge(progress$, done$);
}
